import { Component } from '@theme/component';

/**
 * Weekly-drop countdown. Toggles between the countdown face and the shop-now
 * face based on a CET/CEST drop day and hour.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} countdown
 * @property {HTMLElement} shop
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 *
 * @extends {Component<Refs>}
 */
export class CountdownBanner extends Component {
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();
    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#interval) window.clearInterval(this.#interval);
  }

  get #dropDay() {
    return Number(this.dataset.dropDay ?? 4);
  }

  get #dropHour() {
    return Number(this.dataset.dropHour ?? 18);
  }

  get #shopWindowHours() {
    return Number(this.dataset.shopWindowHours ?? 24);
  }

  /**
   * Offset from the local clock to CET/CEST, in milliseconds.
   */
  #cetOffset() {
    const now = new Date();
    const jan = new Date(now.getFullYear(), 0, 1);
    const jul = new Date(now.getFullYear(), 6, 1);
    const stdOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
    const isDST = now.getTimezoneOffset() < stdOffset;
    const cetHours = isDST ? 2 : 1;
    return (cetHours * 60 - now.getTimezoneOffset()) * 60 * 1000;
  }

  #nextDrop() {
    const now = new Date();
    const cetOffset = this.#cetOffset();
    const nowCET = new Date(now.getTime() + cetOffset);
    const dropDay = this.#dropDay;
    const dropHour = this.#dropHour;
    const shopWindowHours = this.#shopWindowHours;

    const nextDrop = new Date(nowCET);
    nextDrop.setHours(dropHour, 0, 0, 0);

    const currentDay = nowCET.getDay();
    let daysUntilDrop = (dropDay - currentDay + 7) % 7;

    if (currentDay === dropDay && nowCET.getHours() >= dropHour) {
      return { isShopTime: true, endTime: new Date(nextDrop.getTime() + shopWindowHours * 60 * 60 * 1000) };
    }

    if (currentDay === (dropDay + 1) % 7 && nowCET.getHours() < dropHour) {
      const yesterdayDrop = new Date(nowCET);
      yesterdayDrop.setDate(yesterdayDrop.getDate() - 1);
      yesterdayDrop.setHours(dropHour, 0, 0, 0);
      const endTime = new Date(yesterdayDrop.getTime() + shopWindowHours * 60 * 60 * 1000);
      if (nowCET < endTime) {
        return { isShopTime: true, endTime };
      }
    }

    if (daysUntilDrop === 0 && nowCET.getHours() < dropHour) {
      daysUntilDrop = 0;
    } else if (daysUntilDrop === 0) {
      daysUntilDrop = 7;
    }

    nextDrop.setDate(nextDrop.getDate() + daysUntilDrop);
    return { isShopTime: false, dropTime: nextDrop.getTime() - cetOffset };
  }

  #pad(value) {
    return String(value).padStart(2, '0');
  }

  #tick() {
    const dropInfo = this.#nextDrop();
    const { countdown, shop, days, hours, minutes, seconds } = this.refs;

    if (dropInfo.isShopTime) {
      if (countdown) countdown.hidden = true;
      if (shop) shop.hidden = false;
      if (Date.now() >= dropInfo.endTime.getTime()) {
        if (countdown) countdown.hidden = false;
        if (shop) shop.hidden = true;
      }
      return;
    }

    if (countdown) countdown.hidden = false;
    if (shop) shop.hidden = true;

    const distance = dropInfo.dropTime - Date.now();
    if (distance < 0) return;

    if (days) days.textContent = this.#pad(Math.floor(distance / (1000 * 60 * 60 * 24)));
    if (hours) hours.textContent = this.#pad(Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
    if (minutes) minutes.textContent = this.#pad(Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)));
    if (seconds) seconds.textContent = this.#pad(Math.floor((distance % (1000 * 60)) / 1000));
  }
}

if (!customElements.get('countdown-banner')) {
  customElements.define('countdown-banner', CountdownBanner);
}
